// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Minimal hyperscript-style helpers — avoids JSX/templating overhead.

type Child = Node | string | number | null | undefined | false;

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Record<string, string | number | boolean | EventListener | null> | null,
  children: Child | Child[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      } else if (k === 'class' || k === 'className') {
        node.className = String(v);
      } else if (k === 'html') {
        node.innerHTML = String(v);
      } else if (k === 'style' && typeof v === 'string') {
        node.setAttribute('style', v);
      } else {
        node.setAttribute(k, String(v));
      }
    }
  }
  appendChildren(node, children);
  return node;
}

export function svg(tag: string, attrs?: Record<string, string | number | EventListener>, children: Child | Child[] = []): SVGElement {
  const node = document.createElementNS('http://www.w3.org/2000/svg', tag);
  if (attrs) {
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null) continue;
      if (k.startsWith('on') && typeof v === 'function') {
        node.addEventListener(k.slice(2).toLowerCase(), v as EventListener);
      } else {
        node.setAttribute(k, String(v));
      }
    }
  }
  appendChildren(node as unknown as HTMLElement, children);
  return node;
}

function appendChildren(node: HTMLElement, children: Child | Child[]) {
  const arr = Array.isArray(children) ? children : [children];
  for (const c of arr) {
    if (c == null || c === false) continue;
    if (typeof c === 'string' || typeof c === 'number') node.appendChild(document.createTextNode(String(c)));
    else node.appendChild(c);
  }
}

export function clear(node: HTMLElement) {
  while (node.firstChild) node.removeChild(node.firstChild);
}

export function icon(name: 'search' | 'info' | 'close' | 'arrow-up' | 'arrow-down' | 'arrow-right' | 'leaf' | 'menu', size = 18): SVGElement {
  const stroke = 'currentColor';
  const common = { width: String(size), height: String(size), viewBox: '0 0 24 24', fill: 'none', stroke, 'stroke-width': '2', 'stroke-linecap': 'round', 'stroke-linejoin': 'round' };
  const paths: Record<string, string[]> = {
    search: ['<circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/>'],
    info: ['<circle cx="12" cy="12" r="9"/><path d="M12 8h.01"/><path d="M11 12h1v4h1"/>'],
    close: ['<path d="M18 6 6 18"/><path d="m6 6 12 12"/>'],
    'arrow-up': ['<path d="M5 12l7-7 7 7"/><path d="M12 5v14"/>'],
    'arrow-down': ['<path d="M19 12l-7 7-7-7"/><path d="M12 19V5"/>'],
    'arrow-right': ['<path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>'],
    leaf: ['<path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19.5 2c.5 2.5 2 8.4 0 13a8 8 0 0 1-8.5 5z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/>'],
    menu: ['<path d="M4 6h16"/><path d="M4 12h16"/><path d="M4 18h16"/>']
  };
  const node = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  for (const [k, v] of Object.entries(common)) node.setAttribute(k, v);
  node.innerHTML = paths[name].join('');
  return node;
}
